import { Monitor, Moon, Sun } from 'lucide-react'
import { ESCALAS_DE_TEXTO, useEscalaDeTexto, useTema, type Tema } from '@/hooks/useTema'
import { cn } from '@/lib/utils'

const OPCOES: { valor: Tema; label: string; icone: typeof Sun; titulo: string }[] = [
  { valor: 'claro', label: 'Claro', icone: Sun, titulo: 'Tema claro' },
  { valor: 'escuro', label: 'Escuro', icone: Moon, titulo: 'Tema escuro' },
  { valor: 'sistema', label: 'Automatico', icone: Monitor, titulo: 'Seguir o aparelho' },
]

/**
 * Escolha de tema.
 *
 * Tres botoes visiveis em vez de um alternador: num alternador de dois
 * estados, quem configurou o celular para escurecer a noite perde esse
 * comportamento ao tocar uma vez, e nao tem como voltar.
 *
 * `aria-pressed` comunica o estado ao leitor de tela - a cor sozinha nao.
 */
export function SeletorDeTema({ compacto = false }: { compacto?: boolean }) {
  const { tema, definirTema } = useTema()

  return (
    <div
      role="group"
      aria-label="Tema das cores"
      className={cn('flex gap-1', compacto && 'gap-0.5')}
    >
      {OPCOES.map((opcao) => {
        const ativo = tema === opcao.valor
        return (
          <button
            key={opcao.valor}
            type="button"
            onClick={() => definirTema(opcao.valor)}
            aria-pressed={ativo}
            title={opcao.titulo}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-2.5 text-sm font-medium transition-colors',
              compacto ? 'min-h-9' : 'min-h-11 px-3',
              ativo
                ? 'border-primario bg-primario text-sobre-primario'
                : 'border-borda hover:bg-afundado',
            )}
          >
            <opcao.icone className="size-4 shrink-0" aria-hidden="true" />
            {!compacto && opcao.label}
            {compacto && <span className="sr-only">{opcao.label}</span>}
          </button>
        )
      })}
    </div>
  )
}

/** Controle de tamanho de fonte, para quem nao usa o zoom do navegador. */
export function SeletorDeEscala({ compacto = false }: { compacto?: boolean }) {
  const { escala, definirEscala } = useEscalaDeTexto()

  return (
    <div role="group" aria-label="Tamanho do texto" className="flex gap-1">
      {ESCALAS_DE_TEXTO.map((opcao) => (
        <button
          key={opcao.valor}
          type="button"
          onClick={() => definirEscala(opcao.valor)}
          aria-pressed={escala === opcao.valor}
          title={opcao.titulo}
          className={cn(
            'rounded-md border font-semibold transition-colors',
            compacto ? 'min-h-9 min-w-9 px-2 text-sm' : 'min-h-11 min-w-11 px-3',
            escala === opcao.valor
              ? 'border-primario bg-primario text-sobre-primario'
              : 'border-borda hover:bg-afundado',
          )}
        >
          {opcao.label}
          <span className="sr-only"> — {opcao.titulo}</span>
        </button>
      ))}
    </div>
  )
}
